import React, { useMemo, useState, useCallback, useEffect } from 'react';
import _ from 'lodash';
import moment from 'moment-timezone';
import { useAppSelector } from 'careand-redux/store';
import { useAppDispatch } from 'careand-redux/useAppDispatch';
import { ClinicStatisticsNew } from 'generated/api';
import { api } from 'careand-redux/api/ApiThunks';
import PatientUtils from 'services/PatientUtils';
import PhysicianUtils from 'services/PhysicianUtils';
import ATable from 'components/Clinic/ClinicFinancials/ATable';
import { APPOINTMENT_STATUS } from 'services/AppointmentUtils';
import DefaultTooltip from 'components/Utils/DefaultTooltip';
//@ts-ignore TODO make workers work with ts
import AppointmentsWorker from '../../../../workers/appointments.worker?worker';
import useWebWorker from 'workers/useWebWorker';

const headers = [
  '#completed',
  '#no-show',
  <DefaultTooltip title="cancelled on the same day by the patient" placement="top">
    <span>#cancelled</span>
  </DefaultTooltip>,
  'Patient',
  'Primary NP',
];

function PatientsWithMostAppointments({
  data,
  clinicId,
  physicianId,
  sortBy = APPOINTMENT_STATUS.no_show,
}: {
  data: ClinicStatisticsNew;
  clinicId?: string;
  physicianId?: string;
  sortBy?: string;
}) {
  const oneYearAgo = moment().add(-365, 'days').unix();
  const physicians = useAppSelector((state) => state.physicians.byId);
  const patients = useAppSelector((state) => state.patients.byId);
  const [loadingPatients, setLoadingPatients] = useState(false);
  const dispatch = useAppDispatch();
  const [workerResult, setWorkerResult] = useState<any>(undefined);

  const message = useMemo(() => {
    return {
      action: 'getTop100Patients',
      sortBy,
      clinicId,
      physicianId,
      appointments: data?.appointments,
      patients: new Set(Object.keys(patients)),
    };
  }, [data?.appointments, sortBy, clinicId, physicianId, patients]);

  useWebWorker(AppointmentsWorker, message, setWorkerResult);

  useEffect(() => {
    const missingPatients = workerResult?.missingPatients;
    if (!missingPatients || missingPatients.length === 0) {
      return;
    }

    setLoadingPatients(true);
    dispatch(
      api.Patients.getMultiplePatients({ multipleValuesPayload: { values: missingPatients } }),
    ).finally(() => {
      setLoadingPatients(false);
    });
  }, [workerResult?.missingPatients, loadingPatients]);

  const rows = useMemo(() => {
    return _(workerResult?.entries ?? [])
      .map(([id, statusCount]) => {
        const patient = patients[id];
        const np = patient?.primary_physician ? physicians[patient.primary_physician] : undefined;
        const patientName = PatientUtils.getName(patient, 'Loading...', false, false);
        const primaryName = PhysicianUtils.getName(np, 'Loading...');

        return [
          statusCount[APPOINTMENT_STATUS.completed] ?? 0,
          statusCount[APPOINTMENT_STATUS.no_show] ?? 0,
          statusCount[APPOINTMENT_STATUS.cancelled] ?? 0,
          <a href={`/patient/${id}`} className="text-black hover:text-[#2693d1]">
            {patientName}
          </a>,
          <a href={`/staff/${np?.id}`} className="text-black hover:text-[#2693d1]">
            {primaryName}
          </a>,
        ];
      })
      .value();
  }, [workerResult?.entries, patients, physicians]);

  return <ATable headers={headers} rows={rows} />;
}

export default PatientsWithMostAppointments;
